import type { AdminCompanyDetailDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useClipboardCopy } from '@hooks/use-clipboard-copy.hook';
import { useAdminUpdateOrganizationSlug } from '@queries/admin-organizations.query';
import { adminOrganizationSlugSchema } from '@schemas/admin-organization.schema';
import { useStore } from '@tanstack/react-form';
import { safeFormatDateTime } from '@utils/date.util';
import { createFormInvalidHandler } from '@utils/form.util';
import { CheckIcon, CopyIcon, EditIcon, XIcon } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { formatOrganizationTypeLabel } from '../organization-hierarchy.utils';
import { AdminOrganizationSlugFormData, adminOrganizationSlugFormOptions } from './admin-organizations-form-options';

type AdminOrganizationDetailsTileProps = {
  isDeleted: boolean;
  organization: AdminCompanyDetailDto;
  organizationId: string;
};

export const AdminOrganizationDetailsTile: React.FC<AdminOrganizationDetailsTileProps> = (props) => {
  const { isDeleted, organization, organizationId } = props;

  const [isEditing, setIsEditing] = useState(false);
  const { handleCopy, isCopied } = useClipboardCopy();
  const { isPending: isUpdatingSlug, mutateAsync: updateOrganizationSlug } = useAdminUpdateOrganizationSlug();
  const defaultSlugValues = useMemo<AdminOrganizationSlugFormData>(
    () => ({
      slug: organization.slug ?? '',
    }),
    [organization.slug],
  );

  const form = useAppForm({
    ...adminOrganizationSlugFormOptions,
    defaultValues: defaultSlugValues,
    onSubmit: async ({ value }) => {
      const normalizedSlug = value.slug.trim() === '' ? null : value.slug.trim();

      try {
        await updateOrganizationSlug({
          data: {
            slug: normalizedSlug,
          },
          id: organizationId,
        });
        form.reset({ slug: normalizedSlug ?? '' });
        successToast({ description: 'Slug updated successfully.' });
        setIsEditing(false);
      } catch (error) {
        if (error instanceof Error) {
          errorToast({ description: error.message });
          return;
        }

        errorToast({ description: 'Failed to update slug.' });
      }
    },
    onSubmitInvalid: createFormInvalidHandler(),
    validators: {
      onSubmit: adminOrganizationSlugSchema,
    },
  });
  const formSlug = useStore(form.store, (state) => state.values.slug);

  const handleCancelEdit = (): void => {
    form.reset(defaultSlugValues);
    setIsEditing(false);
  };

  const isBusy = isUpdatingSlug;

  return (
    <Tile
      className="border border-gray-200 shadow-none"
      header={{
        actions: isEditing ? (
          <div className="flex gap-1">
            <IconButton
              ariaLabel="Cancel"
              disabled={isBusy}
              icon={<XIcon size={16} />}
              onClick={handleCancelEdit}
              size="md"
              variant="secondary"
            />
            <IconButton
              ariaLabel="Save"
              disabled={isBusy}
              icon={<CheckIcon size={16} />}
              onClick={() => form.handleSubmit()}
              size="md"
              variant="primary"
            />
          </div>
        ) : undefined,
        iconButton: isEditing
          ? undefined
          : {
              disabled: isDeleted,
              disabledTooltip: 'Cannot edit deleted organization',
              icon: <EditIcon size={16} />,
              onClick: () => {
                form.reset(defaultSlugValues);
                setIsEditing(true);
              },
            },
        title: 'Details',
      }}
      hoverReveal={!isEditing}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500">ID</span>
          <div className="flex items-center gap-2">
            <span className="text-gray-900 font-mono text-xs truncate">{organizationId}</span>
            <IconButton
              ariaLabel="Copy ID"
              className="border border-gray-200 shrink-0"
              icon={isCopied('organization-id') ? <CheckIcon /> : <CopyIcon />}
              onClick={() => handleCopy('organization-id', organizationId)}
              size="sm"
              variant="secondary"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500">Name</span>
          <span className="text-sm text-gray-900">{organization.name}</span>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500">Type</span>
          <div className="flex items-center gap-1.5">
            {organization.isParentOrganization && (
              <Badge
                size="xs"
                variant="secondary"
              >
                Parent
              </Badge>
            )}
            {organization.parentOrganizationId && (
              <Badge
                size="xs"
                variant="secondary"
              >
                Child
              </Badge>
            )}
            <span className="text-sm text-gray-900">{formatOrganizationTypeLabel(organization.type)}</span>
          </div>
        </div>

        {isEditing ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.AppForm>
              <form.AppField
                name="slug"
                validators={{ onBlur: adminOrganizationSlugSchema.shape.slug }}
              >
                {(field) => (
                  <div className="flex flex-col gap-1">
                    <field.TextField
                      className="gap-1"
                      disabled={isBusy}
                      inputClassName="font-mono text-sm"
                      label="Slug"
                      onChange={(event) => {
                        field.handleChange(event.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''));
                      }}
                      placeholder="organization-slug"
                    />
                    <span className="text-xs text-gray-500">Lowercase, alphanumeric, hyphens, underscores.</span>
                  </div>
                )}
              </form.AppField>
            </form.AppForm>
          </form>
        ) : (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Slug</span>
            <span className="font-mono text-sm text-gray-900">{formSlug.trim() ? formSlug : '—'}</span>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500">Created</span>
          <span className="text-sm text-gray-900">{safeFormatDateTime(organization.createdAt)}</span>
        </div>
      </div>
    </Tile>
  );
};
